/**
 * Idea Edit API Hooks
 *
 * React Query hooks for unified idea editing with cascade analysis.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query-client';
import apiClient from '../../services/unifiedApiClient';

/**
 * Analyze an idea edit to get cascade suggestions.
 *
 * @returns Mutation that takes { ideaId, section, changes }
 */
export function useAnalyzeIdeaEdit() {
  return useMutation({
    mutationFn: async ({ ideaId, section, changes }) => {
      return await apiClient.post(`/ideas/${ideaId}/edit/analyze`, {
        section,
        changes,
      });
    },
    onError: (error) => {
      console.error('Failed to analyze idea edit:', error);
    },
  });
}

/**
 * Apply an idea edit with approved cascades.
 *
 * @returns Mutation that takes { ideaId, section, changes, approvedCascades }
 */
export function useApplyIdeaEdit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, section, changes, approvedCascades, expectedVersion }) => {
      return await apiClient.post(`/ideas/${ideaId}/edit/apply`, {
        section,
        changes,
        approved_cascades: approvedCascades || [],
        expected_version: expectedVersion ?? null,
      });
    },
    onSuccess: (data, { ideaId }) => {
      // Invalidate idea detail to refresh spec
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });

      // Invalidate ideas list
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.lists() });

      // Invalidate plans since they may have been flagged for refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to apply idea edit:', error);
    },
  });
}

/**
 * Fetch edit history for an idea.
 *
 * @param {string} ideaId
 * @returns Query with edit history data
 */
export function useIdeaEditHistory(ideaId) {
  return useQuery({
    queryKey: [...queryKeys.ideas.detail(ideaId), 'edit-history'],
    queryFn: async () => {
      return await apiClient.get(`/ideas/${ideaId}/edit/history`);
    },
    enabled: !!ideaId,
  });
}

/**
 * Undo the most recent idea edit.
 *
 * @returns Mutation that takes { ideaId }
 */
export function useUndoIdeaEdit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId }) => {
      return await apiClient.post(`/ideas/${ideaId}/edit/undo`);
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to undo idea edit:', error);
    },
  });
}
